import { useState, useCallback, useRef, useEffect, type ReactNode } from "react";
import { Plus, X, Loader2, Wrench, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { uploadImage } from "@/utils/image/upload";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useOwnerChat } from "@/hooks/brain/useOwnerChat";
import { useAuth } from "@/auth/AuthProvider";
import { useRole } from "@/hooks/auth/useRole";
import { useBrain } from "@/contexts/ui/brain";
import { useAutoScroll } from "@/hooks/ui/useAutoScroll";
import { ImpersonationService } from "@/services/users/ImpersonationService";
import BrainLink from "./BrainLink";
import ChatMessage from "./ChatMessage";
import DiagnosticToolResult from "./DiagnosticToolResult";
import EmptyState from "./EmptyState";
import ScrollToBottomFAB from "./ScrollToBottomFAB";
import ThinkingIndicator from "./ThinkingIndicator";
import SessionHistoryPopover from "./SessionHistoryPopover";
import VoiceInputButton from "./VoiceInputButton";
import GenieScopeSelector from "./GenieScopeSelector";
import { useVoiceInput } from "@/hooks/brain/useVoiceInput";
import { useResponsiveLayout } from "@/hooks/ui/useResponsiveLayout";
import { cn } from "@/lib/utils";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const MASCOT_SRC = "/genie-mascots/role/support.png";

const STREAMING_MARKDOWN_COMPONENTS = {
  h1: ({ children }: { children?: ReactNode }) => <h3 className="text-base font-bold mt-2.5 mb-1 text-gray-900">{children}</h3>,
  h2: ({ children }: { children?: ReactNode }) => <h3 className="text-base font-bold mt-2.5 mb-1 text-gray-900">{children}</h3>,
  h3: ({ children }: { children?: ReactNode }) => <h4 className="text-base font-semibold mt-2 mb-0.5 text-gray-900">{children}</h4>,
  p: ({ children }: { children?: ReactNode }) => <p className="mb-1.5 leading-relaxed last:mb-0">{children}</p>,
  ul: ({ children }: { children?: ReactNode }) => <ul className="mb-1.5 pl-4 space-y-0.5 list-disc last:mb-0">{children}</ul>,
  ol: ({ children }: { children?: ReactNode }) => <ol className="mb-1.5 pl-4 space-y-0.5 list-decimal last:mb-0">{children}</ol>,
  li: ({ children }: { children?: ReactNode }) => <li className="text-base">{children}</li>,
  strong: ({ children }: { children?: ReactNode }) => <strong className="font-semibold">{children}</strong>,
  hr: () => <hr className="my-2 border-gray-200" />,
  code: ({ children }: { children?: ReactNode }) => <code className="bg-black/5 px-1 py-0.5 rounded text-xs font-mono">{children}</code>,
  pre: ({ children }: { children?: ReactNode }) => <pre className="bg-black/5 p-2 rounded overflow-x-auto mb-1.5 text-xs">{children}</pre>,
  a: ({ children, href }: { children?: ReactNode; href?: string }) => <BrainLink href={href}>{children}</BrainLink>,
};

interface HelpGenieWidgetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const HelpGenieWidget = ({ open, onOpenChange }: HelpGenieWidgetProps) => {
  const { userProfile } = useAuth();
  const { isInternalAdmin } = useRole();
  const { consumePendingMessage, scope } = useBrain();
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [diagnosticMode, setDiagnosticMode] = useState(false);
  const [pendingImage, setPendingImage] = useState<{ url: string; name: string } | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const showDiagnosticToggle = isInternalAdmin();

  const scopePayload = scope
    ? { genie_id: scope.genieId, genie_name: scope.genieName }
    : null;

  const {
    messages,
    isLoadingMessages,
    sessions,
    sendMessage,
    isSending,
    streamingText,
    streamingStatus,
    streamingToolResults,
    createSession,
    deleteSession,
    retryLastMessage,
    submitFeedback,
  } = useOwnerChat(activeSessionId, open);

  const { scrollRef, bottomRef, scrollToBottom, isAtBottom } = useAutoScroll({ smooth: true });

  const handleVoiceTranscript = useCallback((text: string) => {
    setInputValue((prev) => (prev ? prev + " " + text : text));
  }, []);
  const { isSupported: voiceSupported, isListening, isTranscribing, startListening, stopListening } = useVoiceInput(handleVoiceTranscript);
  const { isMobile, isTabletOrMobile } = useResponsiveLayout();

  const activeSessionRef = useRef(activeSessionId);
  useEffect(() => {
    activeSessionRef.current = activeSessionId;
  }, [activeSessionId]);

  // Hide public widget when HelpGenieWidget is open
  useEffect(() => {
    const v1 = document.getElementById('helpgenie-widget-container');
    const v2 = document.querySelector<HTMLElement>('.hgv2-host');
    const display = open ? 'none' : '';
    if (v1) v1.style.display = open ? 'none' : 'block';
    if (v2) v2.style.display = display;
  }, [open]);

  // Reset active session when impersonation state changes (admin/impersonated user switch)
  const impersonatedUserId = ImpersonationService.getImpersonatedUserId();
  useEffect(() => {
    setActiveSessionId(null);
  }, [impersonatedUserId]);

  // Handle pending message from brain context (e.g. clicked a smart prompt).
  // We pre-fill the textarea instead of auto-sending so the user gets a beat to
  // set Your Genie's scope, tweak the question, or just hit Enter to send.
  const pendingHandledRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (!open || pendingHandledRef.current) return;
    const pending = consumePendingMessage();
    if (!pending) return;
    pendingHandledRef.current = true;

    setInputValue(pending);
    // Focus + move cursor to end after the sheet finishes animating in
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(pending.length, pending.length);
      }
    });

    return () => { pendingHandledRef.current = false; };
  }, [open, consumePendingMessage]);

  const handleImageSelect = useCallback(async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Please attach an image file.");
      return;
    }
    setIsUploadingImage(true);
    try {
      const result = await uploadImage(file, "genie-images");
      if (result.error || !result.url) {
        toast.error(result.error || "Failed to upload image");
        return;
      }
      setPendingImage({ url: result.url, name: file.name });
    } finally {
      setIsUploadingImage(false);
    }
  }, []);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            e.preventDefault();
            void handleImageSelect(file);
          }
          return;
        }
      }
    },
    [handleImageSelect],
  );

  const handleSend = useCallback(async () => {
    if ((!inputValue.trim() && !pendingImage) || isSending) return;
    const image = pendingImage;
    const msg = image
      ? `${inputValue.trim() || "Please look at this image."}\n\n[Attached image: ${image.url}]`
      : inputValue;
    setInputValue("");
    setPendingImage(null);

    let sid = activeSessionRef.current;
    if (!sid) {
      sid = await createSession();
      setActiveSessionId(sid);
      activeSessionRef.current = sid;
    }

    await sendMessage({ message: msg, sessionOverride: sid, scope: scopePayload, diagnostic: diagnosticMode });
  }, [inputValue, pendingImage, isSending, createSession, sendMessage, scopePayload, diagnosticMode]);

  const handleNewChat = useCallback(async () => {
    const sid = await createSession();
    setActiveSessionId(sid);
    setInputValue("");
    setDiagnosticMode(false);
  }, [createSession]);

  const handleSuggestionSelect = useCallback(async (text: string) => {
    if (isSending) return;
    setInputValue("");

    let sid = activeSessionRef.current;
    if (!sid) {
      sid = await createSession();
      setActiveSessionId(sid);
      activeSessionRef.current = sid;
    }

    await sendMessage({ message: text, sessionOverride: sid, scope: scopePayload, diagnostic: diagnosticMode });
  }, [isSending, createSession, sendMessage, scopePayload, diagnosticMode]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const firstName = userProfile?.full_name?.split(" ")[0];

  return (
    <>
      {/* Chat panel */}
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          showClose={false}
          className={cn(
            "w-full flex flex-row h-full p-0 gap-0 border-l-0",
            isMobile
              ? "max-w-full"
              : isTabletOrMobile
                ? "sm:max-w-[calc(100vw-5rem)]"
                : "sm:max-w-[calc(100vw-18rem)]",
          )}
        >
          <SheetTitle className="sr-only">Your Genie</SheetTitle>
          <SheetDescription className="sr-only">Chat with your Help Genie assistant</SheetDescription>

          {/* Gradient left bar — hidden on mobile */}
          {!isMobile && (
            <div className="w-2 flex-shrink-0 bg-gradient-to-b from-genie-primary to-genie-secondary" />
          )}

          {/* Main content column */}
          <div className="flex flex-col flex-1 min-w-0">
          {/* Header */}
          <SheetHeader className="px-4 py-3 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2.5 min-w-0">
                <img
                  src={MASCOT_SRC}
                  className="h-7 w-7 object-contain flex-shrink-0"
                  alt="Help Genie"
                />
                <SheetTitle className="text-xl font-semibold text-[#222B45] flex-shrink-0">
                  Your Genie
                </SheetTitle>
                <div className="min-w-0">
                  <GenieScopeSelector isMobile={isMobile} />
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {showDiagnosticToggle && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className={cn(
                            "flex items-center gap-1.5 rounded-full px-2 py-1 mr-1 cursor-pointer transition-colors",
                            diagnosticMode ? "bg-amber-50 ring-1 ring-amber-300" : "hover:bg-gray-100",
                          )}
                          onClick={() => setDiagnosticMode((v) => !v)}
                        >
                          <Wrench className={cn("h-3.5 w-3.5", diagnosticMode ? "text-amber-600" : "text-gray-400")} />
                          {!isMobile && (
                            <span className={cn("text-xs font-medium", diagnosticMode ? "text-amber-700" : "text-gray-500")}>
                              Diagnostics
                            </span>
                          )}
                          <Switch
                            checked={diagnosticMode}
                            onCheckedChange={setDiagnosticMode}
                            onClick={(e) => e.stopPropagation()}
                            className="scale-75"
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-64 text-xs">
                        Admin-only: investigates platform behavior using internal logs/tables instead of
                        answering from platform guides. Use for "why did X happen" questions.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                <SessionHistoryPopover
                  sessions={sessions}
                  activeSessionId={activeSessionId}
                  onSelectSession={setActiveSessionId}
                  onNewChat={handleNewChat}
                  onDeleteSession={deleteSession}
                />
                <Button
                  variant="ghost"
                  size={isMobile ? "icon" : "sm"}
                  className={cn(
                    "text-gray-500",
                    isMobile ? "h-8 w-8" : "text-sm gap-1",
                  )}
                  onClick={handleNewChat}
                >
                  <Plus className="h-3.5 w-3.5" />
                  {!isMobile && "New"}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onOpenChange(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </SheetHeader>

          {/* Messages area */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 relative">
            {isLoadingMessages ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : messages.length === 0 && !isSending ? (
              <EmptyState
                firstName={firstName}
                onSuggestionSelect={handleSuggestionSelect}
                disabled={isSending}
              />
            ) : (
              messages.map((msg, idx) => (
                <ChatMessage
                  key={msg.id}
                  message={msg}
                  userAvatarUrl={userProfile?.avatar_url}
                  userName={userProfile?.full_name}
                  onFeedback={(id, rating) => submitFeedback(id, rating)}
                  onRetry={retryLastMessage}
                  isLastFailed={
                    msg.role === "user" &&
                    idx === messages.length - 1 &&
                    !!messages[messages.length - 1]?.id.startsWith("temp-")
                  }
                  isMobile={isMobile}
                />
              ))
            )}

            {/* Streaming assistant response */}
            {isSending && streamingText && (
              <div className="flex items-start gap-2.5 justify-start">
                <img src={MASCOT_SRC} className={cn("rounded-full object-cover ring-1 ring-primary/20 flex-shrink-0 mt-0.5", isMobile ? "h-8 w-8" : "h-10 w-10")} alt="Help Genie" />
                <div className={cn("rounded-lg px-4 py-2.5 bg-gray-100 text-gray-900", isMobile ? "max-w-[92%]" : "max-w-[85%]")}>
                  <div className="text-base max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={STREAMING_MARKDOWN_COMPONENTS}>
                      {streamingText}
                    </ReactMarkdown>
                  </div>
                  {/* Completed tool calls pile up below the message text */}
                  {streamingToolResults.length > 0 && (
                    <div className="mt-2 space-y-0.5">
                      {streamingToolResults.map((tr, i) => (
                        <DiagnosticToolResult key={i} tool={tr.tool} summary={tr.summary} />
                      ))}
                    </div>
                  )}
                  {/* Cycling status indicator — always the last item */}
                  {isSending && <ThinkingIndicator inline status={streamingStatus} />}
                </div>
              </div>
            )}

            {/* Thinking / tool-call status indicator (no text yet) */}
            {isSending && !streamingText && (
              <div className="flex items-start gap-2.5 justify-start">
                <img src={MASCOT_SRC} className={cn("rounded-full object-cover ring-1 ring-primary/20 flex-shrink-0 mt-0.5", isMobile ? "h-8 w-8" : "h-10 w-10")} alt="Help Genie" />
                <div className={cn("rounded-lg px-4 py-2.5 bg-gray-100 text-gray-900", isMobile ? "max-w-[92%]" : "max-w-[85%]")}>
                  {/* Completed tool calls pile up above the loader, which is always last */}
                  {streamingToolResults.length > 0 && (
                    <div className="mb-1.5 space-y-0.5">
                      {streamingToolResults.map((tr, i) => (
                        <DiagnosticToolResult key={i} tool={tr.tool} summary={tr.summary} />
                      ))}
                    </div>
                  )}
                  <ThinkingIndicator inline status={streamingStatus} />
                </div>
              </div>
            )}

            <div ref={bottomRef} />

            {/* Scroll to bottom FAB */}
            <ScrollToBottomFAB visible={!isAtBottom} onClick={scrollToBottom} isMobile={isMobile} />
          </div>

          {/* Input area */}
          <div className={cn("border-t border-gray-200 p-3 flex-shrink-0", isMobile && "pb-[calc(0.75rem+env(safe-area-inset-bottom))]")}>
            {pendingImage && (
              <div className="mb-2 inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs text-gray-600">
                <img src={pendingImage.url} alt={pendingImage.name} className="h-8 w-8 rounded object-cover" />
                <span className="max-w-[160px] truncate">{pendingImage.name}</span>
                <button
                  type="button"
                  onClick={() => setPendingImage(null)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Remove attached image"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            <div
              className={cn(
                "relative flex items-center rounded-xl border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-genie-primary transition-shadow",
              )}
            >
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleImageSelect(file);
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                disabled={isSending || isUploadingImage}
                title="Attach an image"
                className="shrink-0 h-11 w-11 ml-1 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {isUploadingImage ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ImagePlus className="h-4 w-4" />
                )}
              </button>
              <Textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder={diagnosticMode ? "Describe what happened — Diagnostics will investigate logs..." : "Ask Help Genie anything, or paste an image..."}
                className={cn(
                  "max-h-[120px] resize-none text-base border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-1",
                  isMobile ? "min-h-[48px]" : "min-h-[56px]",
                )}
                disabled={isSending}
              />
              <div className="flex items-center gap-1 shrink-0 mr-1">
                {voiceSupported && (
                  <VoiceInputButton
                    isListening={isListening}
                    isTranscribing={isTranscribing}
                    onToggle={isListening ? stopListening : startListening}
                    disabled={isSending}
                  />
                )}
                <button
                  onClick={handleSend}
                  disabled={(!inputValue.trim() && !pendingImage) || isSending}
                  className="shrink-0 h-11 w-11 rounded-lg flex items-center justify-center hover:opacity-90 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
                  style={{ background: "linear-gradient(135deg, #4E9CFF 0%, #FF4DB8 100%)" }}
                >
                  <GenieIcon name="genie-lamp" size={20} color="white" label="Send" />
                </button>
              </div>
            </div>
          </div>
          </div>{/* end main content column */}
        </SheetContent>
      </Sheet>
    </>
  );
};

export default HelpGenieWidget;
